import { useState, useEffect, useCallback } from "react";
import { Row, Col, Spinner, Alert } from "react-bootstrap";
import IndicatorCard from "./IndicatorCard";
import DynamicIcon from "../../../common/DynamicIcon";
import api from "../../../services/api";
import { decryptData, tiposAcesso } from "../../../common/utils";
import { programaProp } from "../../../common/propTypes";
import PropTypes from "prop-types";
import { FaChartLine } from "react-icons/fa";

// Função auxiliar de cores mantida
const hexToRgba = (hex, alpha) => {
  if (!hex || hex.length < 4) return "rgba(232, 240, 254, 0.15)";
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// A label de cada bloco é definida aqui, e não pelo `titulo` que a API manda, para
// que o texto exibido possa mudar sem depender de deploy do backend.
const LABEL_POR_TIPO = {
  programa: 'Atendimentos de Pacientes',
  receptivo: 'Atendimentos Gerais',
};

// O backend devolve os indicadores separados em blocos por tipo de atendimento
// ({ tipo_atendimento, titulo, indicadores }). Versões anteriores devolviam a lista
// de indicadores direto, então a resposta antiga vira um bloco único sem título.
const normalizarBlocos = (dados) => {
  if (!Array.isArray(dados) || dados.length === 0) return [];
  if (dados[0]?.indicadores === undefined) {
    return [{ tipo_atendimento: null, titulo: null, indicadores: dados }];
  }
  return dados.filter((bloco) => bloco.indicadores?.length > 0);
};

const CardStatusDetailDashboard = ({ programaId, nomePrograma }) => {
  const [blocos, setBlocos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ... (fetchData e useEffect mantidos iguais ao anterior) ...
  const fetchData = useCallback(async () => {
    if (!programaId) {
      setLoading(false);
      setError("ID do programa não fornecido.");
      return;
    }
    setLoading(true);
    setError(null);
    const dados_outros = decryptData("o");
    const tipo_usuario = dados_outros?.tipo_usuario || 'operador'; // Backend valida is_superuser pelo JWT
    const gestor_id = dados_outros?.gestor_id;
    const cliente_id = dados_outros?.cliente_id || null;

    const params = new URLSearchParams();
    params.append("tipo_usuario", tipo_usuario);
    params.append("programa_id", programaId);
    if (gestor_id) params.append("gestor_id", gestor_id);
    if (cliente_id) params.append("cliente_id", cliente_id);

    try {
      const response = await api.get(
        `/dashboards/status_indicators/?${params.toString()}`,
        {
          headers: {
            'X-Programa-ID': programaId // Header que o backend espera!
          }
        }
      );
      const blocosResposta = normalizarBlocos(response.data);
      if (blocosResposta.length > 0) {
        const mappedBlocos = blocosResposta.map((bloco) => ({
          tipoAtendimento: bloco.tipo_atendimento,
          titulo: LABEL_POR_TIPO[bloco.tipo_atendimento] || bloco.titulo,
          indicators: bloco.indicadores.map((item) => {
            const iconColor = item.cor || "#6c757d";
            return {
              title: item.nome,
              value: item.total_atendimentos,
              change: item.change,
              iconName: item.icon || "HelpCircle",
              filterKey: item.slug,
              iconColor: iconColor,
              iconBgColor: hexToRgba(iconColor, 0.15),
            };
          }),
        }));
        setBlocos(mappedBlocos);
      } else {
        setBlocos([]);
      }
    } catch (err) {
      console.error(err);
      setError("Erro ao carregar indicadores.");
      setBlocos([]);
    } finally {
      setLoading(false);
    }
  }, [programaId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Gestor da indústria não vê os cards de status: eles clicam para a listagem de
  // atendimentos, tela que esse perfil não acessa — ele fica só com os gráficos.
  const dados_usuario_render = decryptData("o");
  if (dados_usuario_render?.tipo_usuario === 'gestor'
      && (tiposAcesso()?.gestor?.tipo_gestor === 'industria')) {
    return null;
  }

  if (loading) return <Spinner animation="border" className="d-block mx-auto my-5" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  
  if (blocos.length === 0) {
    return (
      <Alert variant="info" className="text-center">
        Nenhum indicador de status configurado para este programa/perfil.
      </Alert>
    );
  }

  return (
    <div className="mb-4">
      {nomePrograma ? (
        <Row className="mb-3">
            <Col>
                <h5 className="mb-2" style={{ color: '#1e293b', fontWeight: 600 }}>
                    <FaChartLine className="me-2" style={{ color: '#6366f1' }} />
                    Indicadores: {nomePrograma}
                </h5>
            </Col>
        </Row>
      ) : (
        <Row className="mb-3">
            <Col>
                <h5 className="mb-2" style={{ color: '#1e293b', fontWeight: 600 }}>
                    <FaChartLine className="me-2" style={{ color: '#6366f1' }} />
                    Indicadores
                </h5>
            </Col>
        </Row>
      )}

      {blocos.map((bloco, indice) => (
        <div key={bloco.tipoAtendimento || 'indicadores'} className={indice > 0 ? "mt-4" : ""}>
          {/* com um tipo só o título é redundante: os cards já vêm sob "Indicadores: <programa>" */}
          {blocos.length > 1 && bloco.titulo && (
            <Row className="mb-2">
              <Col>
                <h6 className="mb-0 text-uppercase" style={{ color: '#64748b', fontWeight: 600, letterSpacing: '0.03em' }}>
                  {bloco.titulo}
                </h6>
              </Col>
            </Row>
          )}

          <Row className="g-3">
            {bloco.indicators.map((indicator) => (
              <Col
                key={indicator.filterKey}
                xs={12}
                sm={6}
                lg={4}
                xl={3}
                className="d-flex"
              >
                <IndicatorCard
                  title={indicator.title}
                  value={indicator.value}
                  change={indicator.change}
                  Icon={(props) => <DynamicIcon name={indicator.iconName} {...props} />}
                  filterKey={indicator.filterKey}
                  iconColor={indicator.iconColor}
                  iconBgColor={indicator.iconBgColor}
                  programaId={programaId}
                  tipoAtendimento={bloco.tipoAtendimento}
                />
              </Col>
            ))}
          </Row>
        </div>
      ))}
    </div>
  );
};

CardStatusDetailDashboard.propTypes = {
  ...programaProp,
  nomePrograma: PropTypes.string,
};

export default CardStatusDetailDashboard;
